import { useToast } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import AwsS3 from '@uppy/aws-s3';
import { Restrictions, Uppy, UppyFile } from '@uppy/core';
import { useUppy } from '@uppy/react';
import { useCallback, useEffect, useState } from 'react';
import { UploadFile } from '.';
import { UploadContext } from './useUppyAwsS3UploadParameters';
import useUppyAwsS3UploadParameters from './useUppyAwsS3UploadParameters';
import { ONE_GIGABYTE_IN_BYTES } from 'utils/constants';

interface UseFileUploaderResponse {
  allFiles: UploadFile[];
  completedFiles: UploadFile[];
  failedFileIds: string[];
  uploadProgress: number;
  retryUploads(fileIds?: string[]): void;
  cancelUpload(fileId: string): void;
  reset(): void;
  uploadFile(file: File): Promise<[UppyFile, File]>;
}

export function useFileUploader(
  uploadContext: UploadContext,
  restrictions: Restrictions,
  filesToUpload?: File[],
): UseFileUploaderResponse {
  const buildGetUploadParameters = useUppyAwsS3UploadParameters();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState<UploadFile[]>([]);
  const toast = useToast();

  const uppy = useUppy(() => {
    const updateProgressState = () => {
      setUploadingFiles(uppyInstance.getFiles()); // force state update of file progress attribute
    };
    const uppyInstance = new Uppy({
      meta: { type: 'avatar' },
      debug: true,
      restrictions: {
        maxFileSize: ONE_GIGABYTE_IN_BYTES,
        ...restrictions,
      },
      autoProceed: true,
    });
    uppyInstance.use(AwsS3, {
      getUploadParameters: buildGetUploadParameters({
        uppy: uppyInstance,
        uploadContext,
      }),
    });

    uppyInstance.on('progress', (progress) => {
      updateProgressState();
      setUploadProgress(progress);
    });
    uppyInstance.on('complete', updateProgressState);
    uppyInstance.on('upload', updateProgressState);
    uppyInstance.on('error', (e) => {
      uploadingFiles.forEach((f) => markAsFailedFile(f, e));
      updateProgressState();
    });
    uppyInstance.on('upload-progress', updateProgressState);
    uppyInstance.on('upload-success', updateProgressState);
    uppyInstance.on('upload-retry', updateProgressState);
    uppyInstance.on('upload-error', (file, error) => {
      markAsFailedFile(file, error);
      updateProgressState();
    });
    uppyInstance.on('file-added', updateProgressState);
    uppyInstance.on('files-added', updateProgressState);
    uppyInstance.on('file-removed', updateProgressState);

    return uppyInstance;
  });

  const retryUploads = (fileIds?: string[]) => {
    if (!fileIds) {
      uppy.retryAll();
      setUploadingFiles(uppy.getFiles());
      uppy.setState({ failedFileIds: [] });
    } else {
      fileIds.forEach((fileId) => {
        uppy.retryUpload(fileId);
        unmarkAsFailedFile(fileId);
      });
    }
  };

  const getFailedFileIds: () => string[] = () => {
    return uppy.getState().failedFileIds || [];
  };

  const markAsFailedFile = (file: UploadFile, error: Error) => {
    uppy.setState({
      failedFileIds: Array.from(new Set([...getFailedFileIds(), file.id])),
    });
    uppy.setFileMeta(file.id, {
      error,
      statusCodeError: file.response?.status,
    });
  };

  const unmarkAsFailedFile = (fileId: string) => {
    uppy.setState({
      failedFileIds: getFailedFileIds().filter((fId) => fId !== fileId),
    });
    uppy.setFileMeta(fileId, {
      error: undefined,
      statusCodeError: undefined,
    });
  };

  const isFailedFile = (fileId: string): boolean => {
    return !!getFailedFileIds().find((fId) => fId === fileId);
  };

  const cancelUpload = (fileId: string) => {
    uppy.removeFile(fileId);
    unmarkAsFailedFile(fileId);
  };

  const reset = useCallback(() => {
    uppy.reset();
    setUploadProgress(0);
    setUploadingFiles([]);
  }, [uppy]);

  const uploadFile = useCallback(
    (file: File) => {
      return new Promise<[UppyFile, File]>((resolve, reject) => {
        try {
          const fileId = uppy.addFile({
            source: 'file input',
            name: file.name,
            type: file.type,
            data: file,
          });
          /* Resolve promise only after upload progress is completed */
          const onUploadSuccess = (uppyFile: UppyFile) => {
            if (uppyFile.id === fileId) {
              resolve([uppyFile, file]);
            }
          };
          uppy.on('upload-success', onUploadSuccess);
        } catch (err) {
          if (
            err instanceof Error &&
            err.message.includes('This file exceeds maximum allowed size')
          ) {
            toast({
              title: t`File is too large. Please limit uploads to 1 Gigabyte`,
              status: 'error',
            });
          } else {
            toast({
              title: t`Something went wrong uploading a file`,
              status: 'error',
            });
          }
          reject(err);
        }
      });
    },
    [toast, uppy],
  );

  useEffect(() => {
    (filesToUpload || []).forEach((selectedFile) => {
      if (uppyAlreadyHasFile(uppy.getFiles(), selectedFile)) {
        return;
      }
      uploadFile(selectedFile);
    });
  }, [filesToUpload, uploadFile]);

  return {
    allFiles: uploadingFiles,
    completedFiles: uploadingFiles.filter(
      (f) => !!f.progress?.uploadComplete && !isFailedFile(f.id),
    ),
    failedFileIds: getFailedFileIds(),
    uploadProgress,
    retryUploads,
    cancelUpload,
    reset,
    uploadFile,
  };
}

function uppyAlreadyHasFile(files: UploadFile[], newFile: File): boolean {
  const matchingFile = files.find((f) => f.name === newFile.name);
  return !!matchingFile;
}
